
import { Search, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  selectedTags: string[];
  availableTags: string[];
  onToggleTag: (tag: string) => void;
}

export function SearchBar({ value, onChange, selectedTags, availableTags, onToggleTag }: SearchBarProps) {
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Filter out already selected tags
  const unselectedTags = availableTags.filter(tag => !selectedTags.includes(tag));

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
          <Search className="h-4 w-4" />
        </div>
        <Input 
          type="search"
          placeholder="Search drawings..."
          value={value}
          onChange={handleSearchChange}
          className="pl-9 bg-background/50 hover:bg-background/80 focus:bg-background/90 transition-colors border-border/40 focus-visible:ring-primary/30"
        />
      </div>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="icon"
            className={cn(
              "border-border/40 bg-background/50 hover:bg-background/80",
              selectedTags.length > 0 && "border-primary/30 bg-primary/5 text-primary"
            )}
          >
            <Tag className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-60 p-3 bg-white/95 backdrop-blur-sm border-border/50 shadow-lg rounded-xl"
          align="end"
        >
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Filter by Tags</h4>
            
            {availableTags.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No tags available</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-60 overflow-y-auto p-0.5">
                {availableTags.map(tag => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <Badge 
                      key={tag}
                      variant={isSelected ? "default" : "secondary"}
                      className={cn(
                        "cursor-pointer text-xs py-1 px-2",
                        isSelected
                          ? "bg-primary hover:bg-primary/90"
                          : "bg-muted/60 text-muted-foreground hover:bg-muted"
                      )}
                      onClick={() => onToggleTag(tag)}
                    >
                      {tag}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
